import R from 'ramda';
import { normalizeListing } from 'ha/modules/Algolia';
import { createAction } from 'redux-actions';

import reportError from 'ha/helpers/bugsnag/reportError';
import getTranslatedCollection from 'ha/utils/urls/getTranslatedCollection';
import { getSearchRequestId, getCurrentIndex } from '../selectors';
import { Actions } from '../constants';
import {
  reformatPriceDistribution,
  getPriceDistribution,
  getTrimmedPriceDistribution,
  normalizeMinMaxPrices,
  getAlgoliaFacetQueries,
  getAlgoliaListingsQuery,
  getAlgoliaPinsQuery,
} from '../utils';
import updateSearch from './updateSearch';
import loadCurrencyRates from './loadCurrencyRates';

const flow = {
  start: createAction(Actions.LOAD_START),
  done: createAction(Actions.LOAD_DONE),
  error: createAction(Actions.LOAD_ERROR),
  notFound: createAction(Actions.LOAD_NOT_FOUND),
};

export default (reqParams, { localizedKind, priceAverage }) => (
  dispatch,
  getState,
  services,
) => {
  const requestId = Date.now();

  dispatch(flow.start(requestId));

  return Promise.all([dispatch(updateSearch()), dispatch(loadCurrencyRates())])
    .then(
      ([
        searchParams,
        {
          data: { rates },
        },
      ]) => {
        const algoliaService = services.algolia;

        const indexName = getCurrentIndex(getState());

        return Promise.all([
          Promise.resolve(searchParams),
          Promise.resolve(rates),
          algoliaService
            .searchListings(
              [
                getAlgoliaListingsQuery(reqParams, searchParams, rates),
                ...getAlgoliaFacetQueries(reqParams, searchParams, rates),
                getAlgoliaPinsQuery(reqParams, searchParams, rates),
              ],
              indexName,
            )
            .then(response => response.results),
        ]);
      },
    )
    .then(([searchParams, currencyEuroRates, algoliaResponses]) => {
      const [listings, price, partner, pins] = algoliaResponses;

      if (getSearchRequestId(getState()) !== requestId) {
        // Query is not the last one. Dismiss results.
        return;
      }

      const currencyRate = currencyEuroRates[reqParams.currency];
      const currencyRates = currencyEuroRates;

      const min = R.pathOr(0, ['priceEUR', 'min'], price.facets_stats);
      const max = R.pathOr(0, ['priceEUR', 'max'], price.facets_stats);

      const { minPrice, maxPrice } = normalizeMinMaxPrices(
        min,
        max,
        currencyRate,
      );

      const distributionArg = {
        pricesFacet: price.facets.priceEUR || [],
        minPrice,
        maxPrice,
        rate: currencyRate,
      };

      const priceDistribution = reformatPriceDistribution(
        getPriceDistribution(distributionArg),
      );
      const priceMin = minPrice * 100;
      const priceMax = maxPrice * 100;

      const trimmedPriceDistribution = reformatPriceDistribution(
        getTrimmedPriceDistribution(distributionArg),
      );
      const trimmedPriceMin = priceMin;
      const trimmedPriceMax = R.last(trimmedPriceDistribution).range * 100;

      const langURLListWithCollections = R.mapObjIndexed((url, lang) => {
        const translatedCollection = getTranslatedCollection(
          lang,
          localizedKind,
        );

        return `${url}${
          translatedCollection ? `/${translatedCollection}` : ''
        }`;
      }, searchParams.langURLList);

      dispatch(
        flow.done({
          ...searchParams,
          langURLListWithCollections,
          currency: reqParams.currency,
          edges: listings.hits.map(
            normalizeListing({
              currency: reqParams.currency,
              currencyRate,
            }),
          ),
          pins: pins.hits,
          pageInfo: {
            offset: listings.page,
            total: listings.nbHits,
            pages: listings.nbPages,
            limit: listings.hitsPerPage,
            isExhaustive: Boolean(listings.exhaustiveNbHits),
          },
          listingsCount: listings.nbHits,
          currencyRates,
          priceMin,
          priceMax,
          priceAverage,
          priceDistribution,
          trimmedPriceMin,
          trimmedPriceMax,
          trimmedPriceDistribution,
          listingsSummary: {
            organic: R.pathOr(0, ['facets', 'isPartner', 'false'], partner),
            partner: R.pathOr(0, ['facets', 'isPartner', 'true'], partner),
            isExhaustive: R.pathOr(true, ['exhaustiveFacetsCount'], partner),
          },
        }),
      );
    })
    .catch(error => {
      if (error.status === 404) {
        dispatch(flow.notFound());

        return;
      }
      reportError('Load search results failed', { metaData: { error } });

      dispatch(flow.error());
    });
};
